import { type FC } from "react";

import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import { useWillExtraction } from "@/shared/hooks/useWillExtraction";
import { isExtractionPending } from "@/shared/utils/extractionStatus";
import { useProcessingChecklist } from "@/shared/hooks/useProcessingChecklist";
import { ProcessingChecklist } from "@/shared/components/ProcessingChecklist";

import { LegalAuthoritySidebar } from "./LegalAuthoritySidebar";

import scanDocsAnimation from "@/shared/assets/animations/scan_docs.lottie?url";

const CHECKLIST_ITEMS = [
  "Identifying who the will names to handle the estate",
  "Noting who inherits and what they receive",
  "Checking signing details the court will look for",
] as const;

interface LegalAuthorityWillProcessingStepProps {
  personFirstName: string;
  estateId: number;
  onComplete: () => void;
}

export const LegalAuthorityWillProcessingStep: FC<LegalAuthorityWillProcessingStepProps> = ({
  personFirstName,
  estateId,
  onComplete,
}) => {
  const personName = personFirstName || "your loved one";
  const { data: extraction, dataUpdatedAt } = useWillExtraction(estateId);

  const status = extraction?.status;
  const isTerminal = !!status && !isExtractionPending(status);

  const { activeIndex, isDone } = useProcessingChecklist({
    itemCount: CHECKLIST_ITEMS.length,
    isTerminal,
    dataUpdatedAt,
    onComplete,
  });

  return (
    <div className="flex h-full">
      <LegalAuthoritySidebar activeStep={2} />

      <div className="flex min-w-0 flex-1 flex-col items-center justify-center bg-subtle px-8">
        <div className="flex flex-col items-center text-center">
          <div className="h-[160px] w-[160px]">
            <DotLottieReact src={scanDocsAnimation} loop autoplay />
          </div>

          <h4 className="mt-4">Turning {personName}'s will into next steps</h4>
          <p className="mt-2 text-sm text-secondary">This usually takes under a minute</p>

          <ProcessingChecklist items={CHECKLIST_ITEMS} activeIndex={activeIndex} isDone={isDone} />
        </div>
      </div>
    </div>
  );
};
